import {
  AvatarSizeVariant,
  Badge,
  Button,
  FilterOption,
  HeaderBarProps,
  ManageableItemsList,
  TTableRowAction,
  UserAvatar,
} from '@payaca/ambrosia';
import {
  ComponentProps,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type JSX,
} from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation, useRouteMatch } from 'react-router';
import { Link } from 'react-router-dom';

import useGetMyAccountTeams from '@/api/queries/me/useGetMyAccountTeams';
import { useGetMyAccountUsersExcludedDeactivated } from '@/api/queries/me/useGetMyAccountUsersExcludedDeactivated';
import { UNASSIGNED, encodeTaskAssignee } from '@/helpers/taskAssignee';
import { usePermissions } from '@/hooks/usePermissions';
import CreateEditTaskSidebar from '@/ui/components/createEditTaskSidebar/CreateEditTaskSidebar';

import { AccountsPermissions } from '@payaca/permissions/accounts/accounts.permissions';
import { CustomersPermissions } from '@payaca/permissions/customers/customers.permissions';
import { DealsPermissions } from '@payaca/permissions/deals/deals.permissions';
import { TasksPermissions } from '@payaca/permissions/tasks/tasks.permissions';
import { requestDeleteTask } from '@payaca/store/tasks/tasksActions';
import { SortDirection } from '@payaca/types/listViewTypes';
import { PrelineColourVariant } from '@payaca/types/preline';
import UntitledIcon from '@payaca/untitled-icons';
import { differenceInDays, startOfDay } from 'date-fns';
import useGetMe from '../../../api/queries/me/useGetMe';
import {
  ChecklistTask,
  CustomFieldsTask,
  FormTask,
  GetTasksInput,
  IEntity,
  ITask,
  LegacyFormTask,
  MaterialsListTask,
  Project,
  ScheduledEvent,
  User,
} from '../../../gql/graphql';
import { useAccount } from '@/api/rest/users/useAccount';
import ConfirmDeleteTaskModal from '../confirmDeleteTaskModal/ConfirmDeleteTaskModal';
import ScheduledEventReadDrawer from '../scheduledEventReadDrawer/ScheduledEventReadDrawer';
import TeamAssigneeBadge from '../taskAssignee/TeamAssigneeBadge';

type TBaseTask = {
  id: IEntity['id'];
  name: ITask['name'];
  deadlineDate: ITask['deadline'];
  createdByUserId?: User['id'];
  events: {
    id: ScheduledEvent['id'];
    name: ScheduledEvent['name'];
  }[];
  project?: {
    id: Project['id'];
    publicId: Project['publicId'];
    reference: Project['reference'];
  };
  assignee?: {
    id: User['id'];
    firstName: User['firstName'];
    lastName: User['lastName'];
    email: User['email'];
    colour: User['colour'];
  };
  assigneeTeam?: {
    id: string;
    name: string;
  } | null;
  completionStatus: ITask['completionStatus'];
};
export type ListedTasksTChecklistTask = TBaseTask & {
  __typename: ChecklistTask['__typename'];
};
export type ListedTasksTFormTask = TBaseTask & {
  __typename: FormTask['__typename'];
};
export type ListedTasksTLegacyFormTask = TBaseTask & {
  __typename: LegacyFormTask['__typename'];
};
export type ListedTasksTMaterialsListTask = TBaseTask & {
  __typename: MaterialsListTask['__typename'];
};
export type ListedTasksTCustomFieldsTask = TBaseTask & {
  __typename: CustomFieldsTask['__typename'];
};
export type ListedTasksTTask =
  | ListedTasksTChecklistTask
  | ListedTasksTFormTask
  | ListedTasksTLegacyFormTask
  | ListedTasksTMaterialsListTask
  | ListedTasksTCustomFieldsTask;

const CompletionStatusMap: {
  [key: string]: { label: string; badgeColour: PrelineColourVariant };
} = {
  'in progress': { label: 'In progress', badgeColour: 'yellow' },
  'to do': { label: 'To do', badgeColour: 'blue' },
  complete: { label: 'Done', badgeColour: 'green' },
};

const standardFilterOptions: FilterOption[] = [
  {
    title: 'Status',
    name: 'completionStatuses',
    type: 'checkbox',
    options: [
      { label: 'To do', value: 'to do' },
      { label: 'In progress', value: 'in progress' },
      { label: 'Done', value: 'complete' },
    ],
  },
  {
    title: 'Type',
    name: 'types',
    type: 'checkbox',
    options: [
      {
        label: 'Checklist',
        value: 'checklist',
      },
      {
        label: 'Form',
        value: 'form',
      },
      {
        label: 'Materials list',
        value: 'materials-list',
      },
      {
        label: 'Custom fields',
        value: 'custom-fields',
      },
    ],
  },
  {
    title: 'Event',
    name: 'eventRelationshipStatuses',
    type: 'checkbox',
    options: [
      {
        label: 'Linked',
        value: 'linked',
      },
      {
        label: 'Unlinked',
        value: 'unlinked',
      },
    ],
  },
];

const TaskTypeIconNameMap: Record<
  Exclude<ListedTasksTTask['__typename'], undefined>,
  ComponentProps<typeof UntitledIcon>['name']
> = {
  ChecklistTask: 'check-done-02.3',
  MaterialsListTask: 'list',
  FormTask: 'file-06.3',
  LegacyFormTask: 'file-06.3',
  CustomFieldsTask: 'file-06.3',
};

export type ListedTasksPaginationInput = {
  limit: number;
  offset: number;
};
type Props = {
  getTasksInput: GetTasksInput;
  setGetTasksInput: (updatedGetTasksInput: GetTasksInput) => void;
  getTasksPagination: ListedTasksPaginationInput;
  setGetTasksPagination: (
    updatedGetTasksPagination: ListedTasksPaginationInput
  ) => void;
  onPersistTaskSuccess?: () => void;
  listedTasks?: ListedTasksTTask[];
  isLoadingTasks: boolean;
  hideProjectAndCustomerColumn?: boolean;
  totalCount: number;
  headerContent?: HeaderBarProps;
};

const ListedTasks = ({
  getTasksInput,
  setGetTasksInput,
  getTasksPagination,
  setGetTasksPagination,
  onPersistTaskSuccess,
  listedTasks,
  isLoadingTasks,
  hideProjectAndCustomerColumn = false,
  totalCount,
  headerContent,
}: Props): JSX.Element | null => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { search } = useLocation();
  const { url } = useRouteMatch();
  const account = useAccount();
  const { userHasRequiredPermission } = usePermissions();
  const { data: me } = useGetMe();

  const [showDeleteTaskWithFormModal, setShowDeleteTaskWithFormModal] =
    useState<false | ListedTasksTTask>(false);
  const [showEventDrawer, setShowEventDrawer] = useState<false | number>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [requiresPersistSearchTerm, setRequiresPersistSearchTerm] =
    useState<boolean>(false);
  const [showEditTaskModal, setShowEditTaskModal] = useState<false | number>(
    false
  );
  const { data: activeUsers } = useGetMyAccountUsersExcludedDeactivated({
    hasAnyPermission: ['tasks.getSelfAssignedTasks'],
  });
  const canListTeams = userHasRequiredPermission([
    AccountsPermissions.GET_TEAMS,
  ]);
  // The resolver refuses a caller without the permission, so asking anyway
  // costs a rejected request and a Sentry event per retry, every mount.
  const { data: accountTeams } = useGetMyAccountTeams({
    enabled: canListTeams,
  });

  const canNavigateToProject = userHasRequiredPermission([
    DealsPermissions.GET_MY_DEAL,
  ]);
  const canNavigateToCustomer = userHasRequiredPermission([
    CustomersPermissions.GET_CUSTOMERS,
  ]);

  useEffect(() => {
    if (requiresPersistSearchTerm) {
      setGetTasksInput({ ...getTasksInput, searchTerm });
      setRequiresPersistSearchTerm(false);
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has missing dependencies: 'getTasksInput', and 'setGetTasksInput'
  }, [requiresPersistSearchTerm, searchTerm]);

  const assigneeFilterOptions = useMemo(() => {
    if (!activeUsers) return null;
    return [
      ...activeUsers.map((user) => ({
        label: user.fullName,
        value: encodeTaskAssignee({
          type: 'user',
          userPublicId: user.publicId,
        }),
      })),
      // The filter list is flat with no group headings, so the suffix is
      // what distinguishes a Team from a user of the same name.
      ...(accountTeams ?? []).map((team) => ({
        label: `${team.name} (Team)`,
        value: encodeTaskAssignee({ type: 'team', teamPublicId: team.id }),
      })),
      { label: 'Unassigned', value: UNASSIGNED },
    ];
  }, [activeUsers, accountTeams]);

  // Settled once the Teams have arrived, and straight away for a viewer who
  // will never ask for them.
  const teamsSettled = !canListTeams || !!accountTeams;

  /**
   * Every assignee token the picker offers, or null until both lists have
   * settled. Withholding it while either is outstanding keeps a persisted
   * selection from being judged against a half-built list.
   */
  const offeredAssignees = useMemo(
    () =>
      assigneeFilterOptions && teamsSettled
        ? new Set(assigneeFilterOptions.map((option) => option.value))
        : null,
    [assigneeFilterOptions, teamsSettled]
  );

  // A selection persists across sessions, so it can outlive the user or Team it
  // names. The backend refuses a filter it cannot resolve, which would fail the
  // whole listing every visit until the selection was cleared by hand.
  useEffect(() => {
    if (!offeredAssignees) return;

    const applied = getTasksInput?.assignedTo ?? [];
    const kept = applied.filter((token) => offeredAssignees.has(token));
    if (kept.length === applied.length) return;

    setGetTasksInput({ ...getTasksInput, assignedTo: kept });
  }, [offeredAssignees, getTasksInput, setGetTasksInput]);

  const filterOptions: FilterOption[] = useMemo(() => {
    const o: FilterOption[] = [];
    if (assigneeFilterOptions) {
      o.push({
        title: 'Assignee',
        name: 'assignedTo',
        type: 'checkbox',
        options: assigneeFilterOptions,
      });
    }
    o.push(...standardFilterOptions);
    return o;
  }, [assigneeFilterOptions]);

  const getItemActions = useCallback(
    (task: ListedTasksTTask) => {
      const actions: TTableRowAction<ListedTasksTTask>[] = [];
      if (!me?.user.id || task.__typename === 'MaterialsListTask') {
        return [];
      }
      if (
        userHasRequiredPermission([TasksPermissions.PERSIST_TASK]) ||
        (userHasRequiredPermission([
          TasksPermissions.PERSIST_SELF_CREATED_TASK,
        ]) &&
          task.createdByUserId == me.user.id)
      ) {
        actions.push({
          label: 'Edit',
          onClick: (task) => setShowEditTaskModal(+task.id),
        });
      }

      if (
        userHasRequiredPermission([TasksPermissions.DELETE_TASK]) ||
        (userHasRequiredPermission([
          TasksPermissions.DELETE_SELF_CREATED_TASK,
        ]) &&
          task?.createdByUserId == me?.user.id)
      ) {
        actions.push({
          label: 'Delete',
          onClick: (task) => setShowDeleteTaskWithFormModal(task),
        });
      }
      return actions;
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has unnecessary dependency: onPersistTaskSuccess
    [me, onPersistTaskSuccess, userHasRequiredPermission]
  );

  const onSort = useCallback(
    (field: string, sortDirection: SortDirection) => {
      let sortDirectionInput: 'asc' | 'desc' | undefined;
      if (sortDirection) {
        sortDirectionInput =
          sortDirection === SortDirection.ASCENDING ? 'asc' : 'desc';
      }
      setGetTasksInput({
        ...getTasksInput,
        sortBy: field,
        sortDirection: sortDirectionInput,
      });
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'setGetTasksInput'
    [getTasksInput]
  );

  const dueInDays = useCallback((deadlineDate: Date) => {
    const nowMoment = startOfDay(new Date());
    return differenceInDays(deadlineDate, nowMoment);
  }, []);

  const deadline = useCallback((dueDays: number) => {
    const abs = Math.abs(dueDays);
    const dayOrDays = abs === 1 ? 'day' : 'days';

    if (dueDays === 0) return 'Today';
    if (dueDays < 0) return `${-dueDays} ${dayOrDays} ago`;
    return `In ${dueDays} ${dayOrDays}`;
  }, []);

  let tableSortDirection: SortDirection | undefined;
  if (getTasksInput.sortDirection) {
    tableSortDirection =
      getTasksInput.sortDirection === 'asc'
        ? SortDirection.ASCENDING
        : SortDirection.DESCENDING;
  }

  return (
    <div className="listed-tasks flex flex-col gap-4">
      <div className="listed-tasks-results-container">
        <ManageableItemsList>
          {headerContent && (
            <ManageableItemsList.HeaderBar
              heading={headerContent.heading}
              subHeading={headerContent.subHeading}
              buttons={headerContent.buttons}
            />
          )}
          <ManageableItemsList.ActionBar>
            <ManageableItemsList.ActionBar.SearchInput
              value={searchTerm}
              onChange={(value) => setSearchTerm(value)}
              onChangeTimeout={() => setRequiresPersistSearchTerm(true)}
              onBlur={() => setRequiresPersistSearchTerm(true)}
              changeTimeoutMs={300}
            />
            <ManageableItemsList.ActionBar.AdvancedFilter
              options={filterOptions}
              appliedFilters={{
                assignedTo: getTasksInput?.assignedTo || [],
                completionStatuses: getTasksInput?.completionStatuses || [],
                eventRelationshipStatuses:
                  getTasksInput?.eventRelationshipStatuses || [],
                types: getTasksInput?.types || [],
              }}
              onApplyFilters={(filters) => {
                setGetTasksInput({
                  ...filters,
                  searchTerm,
                });
                // reset to page 1
                setGetTasksPagination({
                  ...getTasksPagination,
                  offset: 0,
                });
              }}
            />
          </ManageableItemsList.ActionBar>
          <ManageableItemsList.Table
            items={listedTasks || []}
            uniqueKey={'id'}
            isLoading={!listedTasks && isLoadingTasks}
            onClickRow={(row) =>
              history.push({ pathname: `${url}/${row.id}`, search })
            }
            itemActions={getItemActions}
            sortBy={
              (getTasksInput?.sortBy as keyof ListedTasksTTask) || undefined
            }
            sortDirection={tableSortDirection}
          >
            <ManageableItemsList.Table.Column
              header="Task"
              field="name"
              render={(name, row: ListedTasksTTask) => (
                <>
                  <div className="flex flex-row items-center">
                    <UntitledIcon
                      name={
                        row.__typename
                          ? TaskTypeIconNameMap[row.__typename]
                          : 'check-done-02.3'
                      }
                      className="mr-4 mt-1.5 h-5 w-5 shrink-0 text-blue-500"
                    />
                    <div className="inline-block max-w-sm truncate">{name}</div>
                  </div>
                </>
              )}
              onSort={onSort}
            />
            <ManageableItemsList.Table.Column
              header="Status"
              field="completionStatus"
              render={(value: string) =>
                CompletionStatusMap?.[value] && (
                  <Badge
                    colour={CompletionStatusMap[value].badgeColour}
                    variant="soft"
                  >
                    {CompletionStatusMap[value].label}
                  </Badge>
                )
              }
              sortByName="status"
              onSort={onSort}
            />
            {!hideProjectAndCustomerColumn && (
              <ManageableItemsList.Table.Column
                className="max-w-0 w-full min-w-24"
                header="Customer"
                field="project"
                render={(project, row) => {
                  const customer =
                    project?.customer || row.events?.[0]?.customer;

                  if (customer) {
                    return (
                      <div className="truncate">
                        {canNavigateToCustomer ? (
                          <Link
                            to={`/customers/${customer.publicId}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {customer.name}
                          </Link>
                        ) : (
                          customer.name
                        )}
                      </div>
                    );
                  }
                }}
              />
            )}
            {!hideProjectAndCustomerColumn && (
              <ManageableItemsList.Table.Column
                header="Project"
                field="project"
                render={(project) =>
                  project && (
                    <div
                      className="max-w-44 truncate"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {canNavigateToProject ? (
                        <Link to={`/projects/${project.publicId}`}>
                          {project.reference}
                        </Link>
                      ) : (
                        project.reference
                      )}
                    </div>
                  )
                }
                sortByName="projectReference"
                onSort={onSort}
              />
            )}
            <ManageableItemsList.Table.Column
              header="Event"
              field="events"
              render={(events) =>
                !!events.length && (
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowEventDrawer(events[0].id);
                    }}
                    variant="link"
                  >
                    <span className="max-w-36 truncate">{events[0].name}</span>
                  </Button>
                )
              }
            />

            <ManageableItemsList.Table.Column
              header="Assignee"
              field="assignee"
              render={(value, row) => {
                if (row.assigneeTeam) {
                  return <TeamAssigneeBadge team={row.assigneeTeam} />;
                }
                return (
                  value && (
                    <UserAvatar
                      sizeVariant={AvatarSizeVariant.XS}
                      user={{
                        firstName: value.firstName || '',
                        lastName: value.lastName || '',
                        emailAddress: value.email || '',
                        userColour: value.colour || '',
                        imgSrc: value.avatarUrl,
                      }}
                      enableUserInfoTooltip
                    />
                  )
                );
              }}
              onSort={onSort}
            />
            <ManageableItemsList.Table.Column
              header="Deadline"
              field="deadlineDate"
              render={(value, row) => {
                const isComplete = row.completionStatus === 'complete';
                if (value && !isComplete) {
                  const dueDays = dueInDays(value);
                  const deadlineText = deadline(dueDays);
                  return (
                    <span className={dueDays <= 0 ? 'text-red-600' : ''}>
                      {deadlineText}
                    </span>
                  );
                }
              }}
              onSort={onSort}
            />
          </ManageableItemsList.Table>
          <ManageableItemsList.PaginationBar
            type="custom"
            pageSize={getTasksPagination.limit || 20}
            currentPage={
              getTasksPagination.offset / getTasksPagination.limit + 1
            }
            totalItems={totalCount}
            onPageChange={(newPage) =>
              setGetTasksPagination({
                ...getTasksPagination,
                offset: (newPage - 1) * getTasksPagination.limit,
              })
            }
          />
        </ManageableItemsList>
      </div>
      <CreateEditTaskSidebar
        taskId={
          typeof showEditTaskModal !== 'boolean' ? showEditTaskModal : undefined
        }
        isOpen={!!showEditTaskModal}
        onClose={() => setShowEditTaskModal(false)}
      />
      <ConfirmDeleteTaskModal
        isOpen={!!showDeleteTaskWithFormModal}
        onClose={() => setShowDeleteTaskWithFormModal(false)}
        onConfirmDeleteTask={() =>
          showDeleteTaskWithFormModal &&
          dispatch(
            requestDeleteTask(+showDeleteTaskWithFormModal.id, () => {
              onPersistTaskSuccess?.();
              setShowDeleteTaskWithFormModal(false);
            })
          )
        }
        taskHasDocument={
          showDeleteTaskWithFormModal &&
          (showDeleteTaskWithFormModal.__typename === 'LegacyFormTask' ||
            showDeleteTaskWithFormModal.__typename === 'FormTask')
        }
      />
      <ScheduledEventReadDrawer
        scheduledEventId={showEventDrawer || undefined}
        isOpen={!!showEventDrawer}
        onClose={() => setShowEventDrawer(false)}
        onUpdateScheduledEventSuccess={onPersistTaskSuccess}
        onDeleteScheduledEventSuccess={onPersistTaskSuccess}
        onUpdateTasksSuccess={onPersistTaskSuccess}
        hideNotes={true}
      />
    </div>
  );
};

export default ListedTasks;
